import { useState, useEffect, useRef } from "react";
import { getParams } from "../../../utils/getParams";
import { TAB_ENUM, iframeTabs } from "../../common/config";
import { getDynamicTabImage, getMediaDynamicTab } from "../../../utils/getDynamicTab";
import { transformMediaDataToItems } from "../../../utils/transformMediaData";
import { v4 as uuidv4 } from 'uuid';
import { getCategory } from "../../../utils/utils";
import useWindowSize from "../../../utils/useWindowSize";
import { isHeroImageModeEnabled, generateHeroImageCarouselItems, normalizeAspectRatio } from "../../../utils/heroImageUtils";
import { IMAGE_COMPRESSOR } from "../../../utils/imageCompressor";
import { applyWhitelabelFaviconFromProductData } from "../../../utils/whitelabelFavicon";
import {
    resolveSpin360Config,
    orderCarousel360Items,
    applySpinPayloadToMediaData,
} from "../../../utils/spin360Params";

const useMediaDataAndTabs = () => {
    const [skuData, setSkuData] = useState({});
    const [skuName, setSkuName] = useState('');
    const [loading, setLoading] = useState(true);
    const [dealerVinId, setDealerVinId] = useState(null);
    const [enterpriseId, setEnterpriseId] = useState(null);
    const [aspectRatio, setAspectRatio] = useState('16/9');
    const [mediaId, setMediaId] = useState(null);
    const [teamId, setTeamId] = useState(null);
    const [executionTime, setExecutionTime] = useState(0)
    const [apiCallFlag, setApiCallFlag] = useState('json');
    const [isFirstSixExt, setIsFirstSixExt] = useState(false);
    const [isSplatHalfLoaded, setIsSplatHalfLoaded] = useState(false);
    const [productFetchTimes, setProductFetchTimes] = useState({
        spin: 0,
        image: 0,
        video: 0
    });
    const queryParams = new URLSearchParams(window.location.search);
    const isDemo = queryParams.get('demo');
    const [isMobile, setIsMobile] = useState(false);
    const [postMessageData, setPostMessageData] = useState(null);
    const [heroImagePostMessageData, setHeroImagePostMessageData] = useState(null);
    const [isHeroImageMode, setIsHeroImageMode] = useState(false);
    const [heroImageIndex, setHeroImageIndex] = useState(null);

    const [selectedTemplate, setSelectedTemplate] = useState("t1");

    const [selectedTab, setSelectedTab] = useState(TAB_ENUM.THREE_SIXTY_VIEW);
    const [tabs, setTabs] = useState([]);

    //carousel items
    const [carouselItems, setCarouselItems] = useState([]);
    const [dynamicTabImage, setDynamicTabImage] = useState([]);
    const { params } = getParams();
    const spin360 = resolveSpin360Config(params);

    const sessionIdRef = useRef(null);
    const isApiCalledRef = useRef(false);
    const processedPostMessageRef = useRef(null);
    const windowSize = useWindowSize();

    //setLoading to false if hero image mode is enabled
    useEffect(() => {
        if (isHeroImageMode) {
            setLoading(false);
        }
    }, [isHeroImageMode]);

    if (!sessionIdRef.current) {
        const storedSessionId = sessionStorage.getItem('sessionId');
        if (storedSessionId) {
            sessionIdRef.current = storedSessionId;
        } else {
            const newSessionId = "session" + uuidv4();
            sessionIdRef.current = newSessionId;
            sessionStorage.setItem('sessionId', newSessionId);
        }
    }

    useEffect(() => {
        if (window.parent && window.parent !== window) {
            window.parent.postMessage({ type: 'IFRAME_READY' }, '*');
          }
        const handlePostMessage = (event) => {
          const { data } = event;
          if (!data || typeof data !== 'object') return;
      
          // ✅ Ignore Webpack / Next.js dev messages
          if (data.type && data.type.startsWith('webpack')) return;
      
          // ✅ Handle only your messages
          if (data.type === 'MEDIA_DATA_READY') {
        
            setPostMessageData(data);
          }
          if (data.type === 'HERO_IMAGE_DATA_READY') {
            setHeroImagePostMessageData(data);
          } 
          if (data.type === 'SPLAT_LOADING_PROGRESS') {
            if (typeof data?.percent === 'number' && data.percent >= 50) {
                setTimeout(() => {
                    setIsSplatHalfLoaded(true);
                }, 1000);
            }
          }
        };
      
        window.addEventListener('message', handlePostMessage);
        return () => window.removeEventListener('message', handlePostMessage);
      }, []);
      

    const fetchMediaFromStorage = async ({ mediaId, product }) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_MEDIA_BUCKET_URL_V3}/m/o/${mediaId}/${product}.json`);
            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error fetching media data:', error);
            throw error;
        }
    }
    const fetchMediaFromStorageV3 = async ({ enterpriseId, skuName, product }) => {
        const startTime = performance.now();
        try {
            let URL = `${process.env.REACT_APP_MEDIA_BUCKET_URL_V3}/common-iframe-sku/${skuName}_${enterpriseId}/${product}.json`
            const response = await fetch(URL);
            const data = await response.json();
            const endTime = performance.now();
            const fetchTime = endTime - startTime;
            
            const productKey = product === 'threeSixty' ? 'spin' : 
                              product === 'image' ? 'image' : 
                              product === 'video' ? 'video' : null;
            
            if (productKey) {
                setProductFetchTimes(prev => ({
                    ...prev,
                    [productKey]: fetchTime
                }));
            }
            
            let keys;
            if (product === 'image') {
                let imageData = data?.imageData;
                if (imageData) {
                    keys = Object.keys(imageData);
                }
                if (keys?.length > 0) {
                    return data;
                }
                else throw new Error('No data found');
            }
            else return data;

        } catch (error) {
            console.error('Error fetching media data:', error);
            throw error;
        }
    }

    const getDataFromJson = async ({ params, spin, catalog, feature_video, threed }) => {
        isApiCalledRef.current = true;
        try {
            const mediaId = params?.mediaId;
            const skuName = params?.skuName;
            const enterpriseId = params?.enterpriseId;
            if (!mediaId && (!skuName || !enterpriseId)) return;

            const promise = []
            const productsPicked = [];
            if (spin) {
                if (mediaId) {
                    promise.push(fetchMediaFromStorage({ mediaId, product: 'spin' }))
                } else {
                    promise.push(fetchMediaFromStorageV3({ enterpriseId, skuName, product: 'threeSixty' }))
                }
                productsPicked.push('360')
            }
            if (catalog) {
                if (mediaId) {
                    promise.push(fetchMediaFromStorage({ mediaId, product: 'catalog' }))
                } else {
                    promise.push(fetchMediaFromStorageV3({ enterpriseId, skuName, product: 'image' }))
                }
                productsPicked.push('image')
            }
            if (feature_video) {
                if (mediaId) {
                    promise.push(fetchMediaFromStorage({ mediaId, product: 'feature_video' }))
                } else {
                    promise.push(fetchMediaFromStorageV3({ enterpriseId, skuName, product: 'video' }))
                }
                productsPicked.push('video')
            }
            if (threed) {
                if (mediaId) {
                    promise.push(fetchMediaFromStorage({ mediaId, product: 'threed' }))
                } else {
                    promise.push(fetchMediaFromStorageV3({ enterpriseId, skuName, product: 'threed' }))
                }
                productsPicked.push('threed')
            }
            const results = await Promise.allSettled(promise);
            const data = results.map((result, index) => {
                if (result.status === 'fulfilled') return result.value;
                console.error('Error fetching product', productsPicked[index], result.reason);
                return null;
            });
            return {
                data: data,
                productsPicked: productsPicked
            };
        } catch (error) {
            console.error('Error fetching media data here:', error);
            return {
                data: null,
                productsPicked: []
            };
        }
    }
    const getDataFromPostMessage = ({ postMessageData, spin, catalog, feature_video, threed }) => {
        try {
            const productsPicked = [];
            const respData = [];
            
            
            if (spin && postMessageData?.data?.spin) {
                respData.push(postMessageData.data.spin);
                productsPicked.push('360');
            }
            if (catalog && postMessageData?.data?.catalog) {
                respData.push(postMessageData.data.catalog);
                productsPicked.push('image');
            }
            if (feature_video && postMessageData?.data?.video) {
                respData.push(postMessageData.data.video);
                productsPicked.push('video');
            }
            if (threed && postMessageData?.data?.threed) {
                respData.push(postMessageData.data.threed);
                productsPicked.push('threed');
            }
            if(heroImagePostMessageData?.data?.hero_image) {
                respData.push(heroImagePostMessageData.data.hero_image);
                productsPicked.push('hero_image');
            }
            
            return { data: respData, productsPicked };
        } catch (error) {
            console.error('Error processing postMessage data:', error);
            return { data: null, productsPicked: [] };
        }
    }

    const getDataFromFallback = async ({ params, spin, catalog, feature_video, threed }) => {
        try {
            let mediaId = params?.mediaId;
            let skuName = params?.skuName;
            let enterpriseId = params?.enterpriseId;
            if (!mediaId && (!skuName || !enterpriseId)) return;
            const productsPicked = [];
            let URL;
            if (mediaId) {
                URL = `${process.env.REACT_APP_BASEURL_API_SPYNE}/api/pv1/media-layer/processed-products?mediaId=${mediaId}`;
            } else {
                URL = `${process.env.REACT_APP_BASEURL_API_SPYNE}/api/pv1/media-layer/processed-products?skuName=${skuName}&enterpriseId=${enterpriseId}`;
            }
            const response = await fetch(URL);
            const data = await response.json();

            let respData = [];
            if (spin && data?.spin) {
                respData.push(data.spin);
                productsPicked.push('360');
            }
            if (catalog && data?.catalog && data?.catalog?.imageData && Object.keys(data?.catalog?.imageData)?.length > 0) {
                respData.push(data.catalog);
                productsPicked.push('image');
            }
            if (feature_video && (data?.featureVideo || data?.feature_video)) {
                respData.push(data.featureVideo || data?.feature_video);
                productsPicked.push('video');
            }
            if (threed && data?.threed) {
                respData.push(data.threed);
                productsPicked.push('threed');
            }
            return { data: respData, productsPicked };
        } catch (error) {
            console.error('Error fetching media data:', error);
            return { data: null, productsPicked: [] };
        }
    }
    const getDynamicTabImageFromArray = (images = []) => {
        const hasExterior = images.some((img) => getCategory(img?.image_category) === "exterior");
        const hasInterior = images.some((img) => getCategory(img?.image_category) === "interior");
        const hasAdditional = images.some((img) => getCategory(img?.image_category) === "additional");

        const tabs = [];
        if (hasExterior) tabs.push(iframeTabs.find((t) => t.label === "Exterior"));
        if (hasInterior) tabs.push(iframeTabs.find((t) => t.label === "Interior"));
        if (hasAdditional) tabs.push(iframeTabs.find((t) => t.label === "Additional"));

        return tabs.filter(Boolean);
    };

    const fetchMediaData = async ({ params, spin = 0, catalog = 0, feature_video = 0, threed = 0, isMobile = false}) => {
        const startTime = performance.now();
        try {
 
            let mediaId = params?.mediaId;
            let skuName = params?.skuName;
            let enterpriseId = params?.enterpriseId;
            if (!mediaId && (!skuName || !enterpriseId)) {
                void applyWhitelabelFaviconFromProductData(null, params, undefined);
                return;
            }
            const spin360ForFetch = resolveSpin360Config(params);
            if (
                !spin360ForFetch.needsSpinFetch &&
                !catalog &&
                !feature_video &&
                !threed
            ) {
                void applyWhitelabelFaviconFromProductData(null, params, undefined);
                return;
            }

            let isMediaPresent = false;

            let resp;
            let data;
            let productsPicked;

            // Check if postMessage data is available first
            if (postMessageData && postMessageData.type === 'MEDIA_DATA_READY') {
             
                resp = getDataFromPostMessage({ postMessageData, spin, catalog, feature_video, threed });
                data = resp?.data;
                productsPicked = resp?.productsPicked;
                setApiCallFlag('postmessage');
            } else if (!isApiCalledRef.current) {
                // Fallback to original logic
                resp = await getDataFromJson({ params, spin, catalog, feature_video, threed });
                data = resp?.data;
                productsPicked = resp?.productsPicked;
                
                if (!isDemo) {
                    const productDataMap = (productsPicked || []).reduce((acc, product, index) => {
                        acc[product] = data?.[index] ?? null;
                        return acc;
                    }, {});
                    const missingProducts = (productsPicked || []).filter((product, index) => !data?.[index]);

                    if (!data || data.length === 0 || data.every(item => item === null) || missingProducts.length > 0) {
                        const fallbackResp = await getDataFromFallback({ params, spin, catalog, feature_video, threed });
                        const fallbackData = fallbackResp?.data || [];
                        const fallbackProducts = fallbackResp?.productsPicked || [];
                        const fallbackMap = fallbackProducts.reduce((acc, product, index) => {
                            acc[product] = fallbackData?.[index] ?? null;
                            return acc;
                        }, {});

                        // Merge fallback data only for missing products
                        data = (productsPicked || []).map((product) => {
                            if (productDataMap[product]) return productDataMap[product];
                            return fallbackMap[product] ?? null;
                        });
                        setApiCallFlag('api');
                    }
                }
            }

            const pm =
                postMessageData?.type === 'MEDIA_DATA_READY' ? postMessageData : undefined;
            void applyWhitelabelFaviconFromProductData(
                Array.isArray(data) ? data : null,
                params,
                pm
            );

            if (!Array.isArray(data)) {
                return;
            }

            const mediaData = {
                '360': null,
                'image': null,
                'video': null,
                '360Int': null,
                'threed': null
            }
            // Function to get image dimensions
            const getImageDimensions = (url) => {
                const newUrl = IMAGE_COMPRESSOR({ url });
                return new Promise((resolve) => {
                    const img = new Image();
                    img.onload = () => {
                        resolve({
                            width: img.width,
                            height: img.height
                        });
                    };
                    img.onerror = () => {
                        resolve({ width: 16, height: 9 }); // default on error
                    };
                    img.src = newUrl;
                });
            };

            // Function to determine closest aspect ratio (4:3 or 16:9)
            const getClosestAspectRatio = (width, height) => {
                if (!width || !height) return '16/9';

                const actualRatio = width / height;
                const ratio16_9 = 16 / 9;  // ≈ 1.778
                const ratio4_3 = 4 / 3;    // ≈ 1.333

                // Calculate which ratio is closer
                const diff16_9 = Math.abs(actualRatio - ratio16_9);
                const diff4_3 = Math.abs(actualRatio - ratio4_3);

                return diff16_9 < diff4_3 ? '16/9' : '4/3';
            };
            // Function to get aspect ratio from images
            const getAspectRatioFromImages = async (imageData) => {
                if (!imageData) return '16/9';

                // If it's an array (preserve incoming order), use the first image
                if (Array.isArray(imageData)) {
                    const firstUrl = imageData?.[0]?.output_image_hres_url;
                    if (firstUrl) {
                        const dimensions = await getImageDimensions(firstUrl);
                        if (dimensions) {
                            return getClosestAspectRatio(dimensions.width, dimensions.height);
                        }
                    }
                    return '16/9';
                }

                // Backward compat: if it's an object, try exterior images first
                if (imageData?.exterior?.[0]?.output_image_hres_url) {
                    const dimensions = await getImageDimensions(imageData?.exterior?.[0]?.output_image_hres_url);
                    if (dimensions) {
                        return getClosestAspectRatio(dimensions.width, dimensions.height);
                    }
                }
                return '16/9'; // Default if no images available
            };

            const getAspectRatioFromSpinImage = async (imageUrl) => {
                if (!imageUrl) return '16/9';
                const dimensions = await getImageDimensions(imageUrl);
                if (dimensions) {
                    return getClosestAspectRatio(dimensions.width, dimensions.height);
                }
                return '16/9';
            }

            const spin360Resolved = resolveSpin360Config(params);
            const teamConfig = {
                "image": {
                    "isActive": catalog ? true : false,
                    "position": catalog
                },
                "video": {
                    "isActive": feature_video ? true : false,
                    "position": feature_video,
                    // Optional: drop the video inside the image set at this 1-based
                    // slot (e.g. 3 or 5) instead of its own block. Falls back to
                    // the legacy block ordering above when unset.
                    "imageSlot": Number(params?.video_slot) || 0
                },
                "threed": {
                    "isActive": threed ? true : false,
                    "position": threed
                },
            };
            if (spin360Resolved.showExterior) {
                teamConfig["360"] = {
                    isActive: true,
                    position: spin360Resolved.exteriorPosition,
                };
            }
            if (spin360Resolved.showInterior) {
                teamConfig["360Int"] = {
                    isActive: true,
                    position: spin360Resolved.interiorPosition,
                };
            }
            // Preserve catalog image order as it comes from API (no re-bucketing / ordering)
            let checkFirstSix = false;

            for (let i = 0; i < data.length; i++) {
                if (data[i]) {
                    isMediaPresent = true;
                    if (productsPicked[i] === '360') {
                        // if (data[i]?.skuId) {
                        //     // const hotspotData = await getHotspotData(data[i]?.skuId);
                        //     // if (hotspotData && hotspotData?.hotspots?.length) {
                        //     //     newData = {
                        //     //         ...data[i],
                        //     //         hotspots: hotspotData?.hotspots?.[0]
                        //     //     }
                        //     // }
                        // }
                        applySpinPayloadToMediaData(
                            mediaData,
                            data[i],
                            spin360Resolved
                        );

                    } else if (productsPicked[i] === 'image') {
                        let images = data[i]?.imageData;
                        mediaData['image'] = images || [];
                    } else if (productsPicked[i] === 'video') {
                        mediaData['video'] = data[i]
                        // @todo: remove this fixed iframe url after testing 
                        let videoId = data[i]?.video_id || data[i]?.videoData?.[0]?.video_id;
                        const skuId = data[i]?.sku_id;
                        if(skuId?.length > 0){
                            mediaData['video'].iFrameUrl = `${process.env.REACT_APP_VIDEO_IFRAME_DOMAIN}/video?sku_id=${skuId}&notVerified${isDemo ? '&view_mode_switcher' : ''}`
                        }else if (videoId) {
                            mediaData['video'].iFrameUrl = `${process.env.REACT_APP_VIDEO_IFRAME_DOMAIN}/video?video_id=${videoId}&notVerified`
                        } else {
                            mediaData['video'].iFrameUrl = null;
                        }
                    } else if (productsPicked[i] === 'threed') {
                        const iFrameUrl = data[i]?.output_url || data[i]?.iFrameUrl || null;
                        mediaData['threed'] = {
                            ...data[i],
                            iFrameUrl
                        };
                    }
                    if (data[i]?.dealerVinId) {
                        setDealerVinId(data[i]?.dealerVinId);
                    }
                    if (data[i]?.enterpriseId) {
                        setEnterpriseId(data[i]?.enterpriseId);
                    }
                    if (data[i]?.teamId) {
                        setTeamId(data[i]?.teamId);
                        sessionStorage.setItem('teamId', data[i]?.teamId);
                    }
                }
            }
            setIsFirstSixExt(checkFirstSix);

            // Set aspect ratio based on available images
            let aspectRatio = '16/9';
            if (productsPicked.includes('360') && mediaData['360']) {
                aspectRatio = await getAspectRatioFromSpinImage(mediaData?.['360']?.skuDetails[0]?.output_image_hres_url);
            }else if(productsPicked.includes('image') && mediaData.image) {
                aspectRatio = await getAspectRatioFromImages(mediaData.image);
            }  
            setAspectRatio(aspectRatio);

       
            if (threed && mediaData?.threed) {
                const iFrameUrl = mediaData?.threed?.iFrameUrl
                    || mediaData?.threed?.output_url
                    || null;
                mediaData['threed'] = {
                    ...mediaData['threed'],
                    iFrameUrl
                };
            }

            mediaData['team_config'] = teamConfig
            mediaData['isMediaPresent'] = isMediaPresent;
            
            //store all media data inside skuData
            setSkuData(prev => {
                if (!prev || Object.keys(prev).length === 0) return mediaData;
                const next = {
                    ...prev,
                    ...mediaData,
                };
                // Prevent threed iframe from being cleared by partial updates
                if (threed && !productsPicked?.includes('threed') && prev?.threed) {
                    next.threed = prev.threed;
                }
                return next;
            })
           
            // Create items array for carousel
            const items = orderCarousel360Items(
                transformMediaDataToItems(mediaData, checkFirstSix, isMobile),
                { legacyIntAfterExt: spin360Resolved.legacyIntAfterExt }
            );

            setCarouselItems(items);
            
            //set visible tabs and selected tab based on team config //false because image has no category
        const tabsBasedOnConfig =  getMediaDynamicTab(mediaData, checkFirstSix) || [];
            setTabs(tabsBasedOnConfig);
            setDynamicTabImage(Array.isArray(mediaData?.image) ? getDynamicTabImageFromArray(mediaData.image) : (getDynamicTabImage(mediaData.image) || []));
            if (tabsBasedOnConfig.length > 0) {
                setSelectedTab(tabsBasedOnConfig[0]?.key);
            }

        } catch (error) {
            console.error('Error fetching media data:', error);
        } finally {
            const endTime = performance.now();
            const executionTime = endTime - startTime;
            
            setLoading(false);
            setExecutionTime(executionTime);
        }
  };

    const getHeroImageUrl = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_MEDIA_BUCKET_URL_V3}/hero-image-media/${params?.enterpriseId}_${params?.skuName}_${params?.teamId}.json`);
            const data = await response.json();
            return data ? data : '';
        } catch (error) {
            console.error('Error fetching hero image:', error);
            return '';
        }
    }

    // Handle hero image mode - creates shimmer placeholders with hero image at index 1
    const handleHeroImageMode = async () => {
        let heroImageUrl = null;
        let mediaId = null;
        let aspectRatioSpin = null;
        let aspectRatioImage = null;
        const startTime = performance.now();
        try { 
            if(heroImagePostMessageData?.data?.heroImage?.hero_image) {
                heroImageUrl = postMessageData?.data?.heroImage?.hero_image;
                mediaId = heroImagePostMessageData?.data?.heroImage?.mediaId;
                aspectRatioSpin = heroImagePostMessageData?.data?.heroImage?.aspectRatioSpin;
                aspectRatioImage= heroImagePostMessageData?.data?.heroImage?.aspectRatioImage;
            }   
            else {
                // const data = {}
                const data = await getHeroImageUrl();
                heroImageUrl = data?.hero_image;
                mediaId = data?.mediaId;
                aspectRatioSpin = data?.aspectRatioSpin;
                aspectRatioImage = data?.aspectRatioImage;
            }
            const heroCarouselItems = generateHeroImageCarouselItems(params, heroImageUrl);
            
            // Set hero image index (the position where the real image is placed)
            const heroIndex = 1; // This should match the heroImageIndex in heroImageUtils
            setHeroImageIndex(heroIndex);
            
            // Set carousel items
            setCarouselItems(heroCarouselItems);
            
            // Create minimal skuData for hero image mode
            const heroSkuData = {
                image: {
                    exterior: heroCarouselItems.filter(item => !item.isShimmer).map(item => item.data)
                },
                team_config: {
                    image: {
                        isActive: true,
                        position: 1
                    }
                },
                isMediaPresent: true
            };
            
            setSkuData(heroSkuData);
            
            // Set tabs for image view only
            const heroTabs = [{
                key: TAB_ENUM.GALLERY_VIEW,
                label: 'Images',
                isActive: true
            }];
            setTabs(heroTabs);
            setSelectedTab(TAB_ENUM.GALLERY_VIEW);
            
            // Set aspect ratio (backend sends "4:3" style values — normalize to valid CSS "4/3")
            setAspectRatio(normalizeAspectRatio(aspectRatioSpin) || normalizeAspectRatio(aspectRatioImage) || '16/9');

            const pmHero =
                postMessageData?.type === 'MEDIA_DATA_READY' ? postMessageData : undefined;
            void applyWhitelabelFaviconFromProductData(null, params, pmHero);
            
        } catch (error) {
            console.error('Error in hero image mode:', error);
        } finally {
            const endTime = performance.now();
            const executionTime = endTime - startTime;
            
            // Loading is already set to false immediately when hero image mode is detected
            setExecutionTime(executionTime);
        }
    };

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const isMobParam = queryParams.get('isMob');
    const deviceWidth = windowSize.deviceWidth;
    const shouldBeMobile =
      isMobParam === 'true' ||
      (deviceWidth < 480 && (isMobParam === 'true' || isMobParam == null));
    setIsMobile(!!shouldBeMobile);
  }, [windowSize]);

    // Separate effect for initial data loading - only runs once when params are available
    useEffect(() => {
        if (params?.mediaId || (params?.skuName && params?.enterpriseId)) {
            setSkuName(params?.skuName || '');
            setEnterpriseId(params?.enterpriseId || '');
            setMediaId(params?.mediaId || '');
            
            // Check if hero image mode is enabled
            const heroImageMode = isHeroImageModeEnabled(params);
            setIsHeroImageMode(heroImageMode);
            
            if (heroImageMode) {
                // Handle hero image mode
                handleHeroImageMode();
            } else {
                // Normal mode - fetch media data as usual
                const spinFetchFlag = spin360.needsSpinFetch ? 1 : 0;
                fetchMediaData({ 'params': params, 'spin': spinFetchFlag, 'catalog': Number(params?.catalog), 'feature_video': Number(params?.feature_video), 'threed': Number(params?.threed), 'isMobile': isMobile });
            }
        }
        if (params?.template === "t2") {
            setSelectedTemplate("t2");
        } else {
            setSelectedTemplate("t1");
        }
    }, []); // Only run once on mount

    // Separate effect for handling postMessage data changes
    useEffect(() => {
        if (postMessageData && postMessageData.type === 'MEDIA_DATA_READY' && 
            (params?.mediaId || (params?.skuName && params?.enterpriseId)) &&
            processedPostMessageRef.current !== postMessageData) {
            // Only refetch if we have new postMessage data that hasn't been processed
            processedPostMessageRef.current = postMessageData;
            const heroImageMode = isHeroImageModeEnabled(params);
            if (heroImageMode) {
                handleHeroImageMode();
            } else {
                const spinFetchFlag = spin360.needsSpinFetch ? 1 : 0;
                fetchMediaData({ 'params': params, 'spin': spinFetchFlag, 'catalog': Number(params?.catalog), 'feature_video': Number(params?.feature_video), 'threed': Number(params?.threed), 'isMobile': isMobile });
            }
        }
    }, [postMessageData]);

    // Separate effect for isMobile changes - only update carousel items, don't refetch data
    useEffect(() => {
        if (skuData && Object.keys(skuData).length > 0 && !loading) {
            // Re-transform existing data for mobile/desktop view without refetching
            const items = orderCarousel360Items(
                transformMediaDataToItems(skuData, isFirstSixExt, isMobile),
                { legacyIntAfterExt: spin360.legacyIntAfterExt }
            );
            setCarouselItems(items);
        }
    }, [isMobile]);
    return { skuData, loading, isFirstSixExt, selectedTemplate, selectedTab, setSelectedTab, tabs, carouselItems, dynamicTabImage, dealerVinId, enterpriseId, aspectRatio, skuName, mediaId, sessionId: sessionIdRef?.current, teamId, executionTime, apiCallFlag, isMobile, productFetchTimes, isHeroImageMode, heroImageIndex, postMessageData, isSplatHalfLoaded };
}

export default useMediaDataAndTabs;